// Onboarding intake (docs/superpowers/specs/2026-10-29-onboarding-intake-form-design.md).
// Payload types mirror workers/billing-api/src/onboarding/payload.ts; billing-api checks
// them and tools/onboard's parse_manifest decides, so this module is for the person
// filling in the form, not a security boundary.
import type { ParentAssetMatch } from '../api/client';

export type Utility = 'electricity' | 'water';
export interface OnboardingMeter { serial: string; make: string; breaker_size_amps: number | null; breaker_phases: 1 | 3 | null; factor: number | null }
export interface OnboardingGateway { connection: 'modem' | 'pi'; serial: string; utility: Utility; room: string; group: string; modems: string[]; meters: OnboardingMeter[] }
export interface OnboardingSite { city: string; building: string; rooms: string[]; gateways: OnboardingGateway[] }
export interface OnboardingUser { email: string; portal_redirect: boolean }
export interface OnboardingPocket { name: string; account_number: string }
export interface OnboardingPayload {
  schema: 1; realm: string; friendly_name: string; features: { billing_app: boolean; demand_calc: boolean };
  municipality_id: string | null; sites: OnboardingSite[]; users: OnboardingUser[]; pockets: OnboardingPocket[]; notes: string;
}
export interface OnboardingRequest {
  id: string; realm: string; status: 'draft' | 'submitted'; payload: OnboardingPayload; created_by: string;
  created_at: string; updated_at: string; submitted_at: string | null;
}
export interface OnboardingRequestSummary {
  id: string; realm: string; status: 'draft' | 'submitted'; friendly_name: string;
  counts: { sites: number; gateways: number; meters: number; users: number; pockets: number };
  created_by: string; updated_at: string; submitted_at: string | null;
}
export interface OnboardingReadiness { or_service_account: boolean; binding: string; committed_serials: string[] }

// ---- Form state: every input is text; a key keeps React rows stable across removals.
let seq = 0;
const nextKey = () => `ob${++seq}`;

export interface FormMeter { key: string; serial: string; make: string; breaker_size_amps: string; breaker_phases: '' | '1' | '3'; factor_text: string }
export interface FormRoom { key: string; name: string }
/** A gateway belongs to a room by the room's key, so renaming a room, even through another room's
 *  name on the way, carries exactly its own gateways. room_key '' (or a key no room has) is "not in
 *  a room yet". The payload still names the room: toPayload writes the room's name.
 *  former_room: the room name an older draft gave a gateway that is not one of its site's rooms,
 *  sent back unchanged until the gateway is placed, so re-saving a draft never loses it. */
export interface FormGateway {
  key: string; connection: 'modem' | 'pi'; serial: string; utility: Utility; room_key: string; former_room: string; group: string; meters: FormMeter[];
}
export interface FormSite { key: string; city: string; building: string; rooms: FormRoom[]; gateways: FormGateway[] }
export interface FormUser { key: string; email: string }
export interface FormPocket { key: string; name: string; account_number: string }
export interface OnboardingForm {
  realm: string; friendly_name: string; billing_app: boolean; demand_calc: boolean; municipality_id: string;
  sites: FormSite[]; users: FormUser[]; pockets: FormPocket[]; notes: string;
}

export const emptyMeter = (): FormMeter => ({ key: nextKey(), serial: '', make: '', breaker_size_amps: '', breaker_phases: '', factor_text: '' });
export const emptyRoom = (name = ''): FormRoom => ({ key: nextKey(), name });
export const emptyGateway = (roomKey = '', utility: Utility = 'electricity'): FormGateway => ({
  key: nextKey(), connection: 'modem', serial: '', utility, room_key: roomKey, former_room: '', group: '', meters: [],
});
export const emptySite = (): FormSite => ({ key: nextKey(), city: '', building: '', rooms: [emptyRoom('Electricity'), emptyRoom('Water')], gateways: [] });
export const emptyUser = (): FormUser => ({ key: nextKey(), email: '' });
export const emptyPocket = (): FormPocket => ({ key: nextKey(), name: '', account_number: '' });
export const emptyForm = (): OnboardingForm => ({
  realm: '', friendly_name: '', billing_app: true, demand_calc: true, municipality_id: '', sites: [], users: [], pockets: [], notes: '',
});

/** What a gateway added to this room starts as: the room's name says, else the room's last gateway, else electricity. */
export function defaultUtility(room: FormRoom, gateways: FormGateway[]): Utility {
  if (/water/i.test(room.name)) return 'water';
  if (/electric/i.test(room.name)) return 'electricity';
  const inRoom = gateways.filter((g) => g.room_key === room.key);
  return inRoom.length ? inRoom[inRoom.length - 1].utility : 'electricity';
}

/** The room a gateway is in, or undefined when it is not in one yet. */
export const roomOf = (site: FormSite, gateway: FormGateway): FormRoom | undefined =>
  site.rooms.find((r) => r.key === gateway.room_key);

/** The id of a gateway's "new meter" input, which Submit's check focuses. */
export const newMeterInputId = (gatewayKey: string) => `ob-newmeter-${gatewayKey}`;

export type SubmitCheckItem =
  | { kind: 'gateway'; key: string; serial: string; utility: Utility; room: string }
  | { kind: 'site'; key: string; building: string }
  | { kind: 'request'; key: 'request' };

/** What Submit asks about before sending: allowed, but usually a slip (a gateway without meters can be
 *  commissioned later, so it is a check, not an error). Save draft never runs it. */
export function submitCheck(f: OnboardingForm): SubmitCheckItem[] {
  if (f.sites.length === 0) return [{ kind: 'request', key: 'request' }];
  return f.sites.flatMap((s, i): SubmitCheckItem[] => {
    if (s.gateways.length === 0) return [{ kind: 'site', key: s.key, building: s.building.trim() || String(i + 1) }];
    return s.gateways.flatMap((g, j): SubmitCheckItem[] => (g.meters.length > 0 ? [] : [{
      kind: 'gateway', key: g.key, serial: g.serial.trim() || String(j + 1), utility: g.utility, room: roomOf(s, g)?.name.trim() ?? '',
    }]));
  });
}

/** "300" or a CT ratio "1500/5" -> 300; "" -> null (no CTs: Factor stays empty, read as 1). */
export function parseFactor(text: string): { ok: true; value: number | null } | { ok: false; error: string } {
  const t = text.trim();
  if (!t) return { ok: true, value: null };
  const m = /^(\d+(?:\.\d+)?)(?:\s*\/\s*(\d+(?:\.\d+)?))?$/.exec(t);
  if (!m) return { ok: false, error: 'Enter a number (300) or a CT ratio (1500/5)' };
  const value = m[2] === undefined ? Number(m[1]) : Number(m[1]) / Number(m[2]);
  if (!Number.isFinite(value) || value <= 0) return { ok: false, error: 'The factor must be above 0' };
  return { ok: true, value: Math.round(value * 1e6) / 1e6 };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** errors: what a submit would refuse. blocking: what a draft cannot even store (unreadable numbers). */
export function toPayload(f: OnboardingForm): { payload: OnboardingPayload; errors: string[]; blocking: string[] } {
  const errors: string[] = [];
  const blocking: string[] = [];
  const need = (v: string, what: string) => {
    const t = v.trim();
    if (!t) errors.push(`${what} is required`);
    return t;
  };
  if (!f.realm) errors.push('Pick a realm');
  const meterSerials = new Set<string>();
  const sites = f.sites.map((s, i): OnboardingSite => {
    const sw = `Site ${i + 1}`;
    const rooms = s.rooms.map((r) => r.name.trim()).filter(Boolean);
    if (rooms.length === 0) errors.push(`${sw}: add at least one room`);
    s.rooms.forEach((r, k) => { if (!r.name.trim()) errors.push(`${sw}, room ${k + 1}: name it or remove it`); });
    // A name used twice would make a gateway's room ambiguous to billing-api and the onboarding tool.
    [...new Set(rooms.filter((r, k) => rooms.indexOf(r) !== k))].forEach((r) => errors.push(`${sw}: room ${r} appears twice`));
    return {
      city: need(s.city, `${sw} city`), building: need(s.building, `${sw} building`), rooms,
      gateways: s.gateways.map((g, j): OnboardingGateway => {
        const gw = `${sw}, gateway ${j + 1}`;
        const serial = need(g.serial, `${gw} serial`);
        // A gateway has one serial. For a modem it is also the modem frontdoor allows; a Pi has no modem.
        const modems = g.connection === 'modem' && serial ? [serial] : [];
        const room = roomOf(s, g);
        if (!room) errors.push(`${gw}: pick one of the site's rooms`);
        const elec = g.utility === 'electricity';
        return {
          connection: g.connection, serial, utility: g.utility, room: room ? room.name.trim() : g.former_room, group: g.group.trim() || serial, modems,
          meters: g.meters.map((m, k): OnboardingMeter => {
            const mw = `${gw}, meter ${k + 1}`;
            const ms = need(m.serial, `${mw} serial`);
            if (ms && meterSerials.has(ms)) errors.push(`Meter serial ${ms} appears twice`);
            if (ms) meterSerials.add(ms);
            const factor = parseFactor(m.factor_text);
            if (elec && !factor.ok) blocking.push(`${mw}: ${factor.error}`);
            const amps = m.breaker_size_amps.trim();
            const ampsN = amps ? Number(amps) : null;
            if (elec && ampsN !== null && !(Number.isFinite(ampsN) && ampsN > 0)) blocking.push(`${mw}: breaker size must be a number above 0`);
            return {
              serial: ms, make: m.make.trim(),
              breaker_size_amps: elec && ampsN !== null && Number.isFinite(ampsN) && ampsN > 0 ? ampsN : null,
              breaker_phases: elec && m.breaker_phases ? (Number(m.breaker_phases) as 1 | 3) : null,
              factor: elec && factor.ok ? factor.value : null,
            };
          }),
        };
      }),
    };
  });
  const users = f.users.map((u, i): OnboardingUser => {
    const email = need(u.email, `User ${i + 1} email`).toLowerCase();
    if (email && !EMAIL.test(email)) errors.push(`User ${i + 1}: not an email address`);
    // Always on: it writes the user's USER_REALMS key, which client-portal needs to route them.
    return { email, portal_redirect: true };
  });
  const pockets = f.pockets.map((p, i): OnboardingPocket => ({
    name: need(p.name, `Pocket ${i + 1} name`), account_number: need(p.account_number, `Pocket ${i + 1} account number`),
  }));
  return {
    payload: {
      schema: 1, realm: f.realm, friendly_name: need(f.friendly_name, 'Display name'),
      features: { billing_app: f.billing_app, demand_calc: f.demand_calc },
      municipality_id: f.municipality_id || null, sites, users, pockets, notes: f.notes,
    },
    errors: [...blocking, ...errors],
    blocking,
  };
}

export function fromPayload(p: OnboardingPayload): OnboardingForm {
  return {
    realm: p.realm, friendly_name: p.friendly_name, billing_app: p.features.billing_app, demand_calc: p.features.demand_calc,
    municipality_id: p.municipality_id ?? '', notes: p.notes,
    sites: p.sites.map((s) => {
      const rooms = s.rooms.map((name) => emptyRoom(name));
      // A gateway joins the first room its name matches; an empty or unknown room leaves it unplaced.
      const roomKey = (name: string) => (name.trim() ? rooms.find((r) => r.name.trim() === name.trim())?.key ?? '' : '');
      return {
        key: nextKey(), city: s.city, building: s.building, rooms,
        gateways: s.gateways.map((g) => {
          const room_key = roomKey(g.room);
          return {
            key: nextKey(), connection: g.connection, serial: g.serial, utility: g.utility, room_key, former_room: room_key ? '' : g.room,
            group: g.group === g.serial ? '' : g.group,
            meters: g.meters.map((m) => ({
              key: nextKey(), serial: m.serial, make: m.make,
              breaker_size_amps: m.breaker_size_amps === null ? '' : String(m.breaker_size_amps),
              breaker_phases: m.breaker_phases === null ? '' : (String(m.breaker_phases) as '1' | '3'),
              factor_text: m.factor === null ? '' : String(m.factor),
            })),
          };
        }),
      };
    }),
    users: p.users.map((u) => ({ key: nextKey(), email: u.email })),
    pockets: p.pockets.map((q) => ({ key: nextKey(), name: q.name, account_number: q.account_number })),
  };
}

export interface PlannedMeter { site: OnboardingSite; gateway: OnboardingGateway; meter: OnboardingMeter }
export const plannedMeters = (p: OnboardingPayload): PlannedMeter[] =>
  p.sites.flatMap((site) => site.gateways.flatMap((gateway) => gateway.meters.map((meter) => ({ site, gateway, meter }))));

export const assetTypeFor = (u: Utility) => (u === 'electricity' ? 'ElectricityConsumerAsset' : 'GroundwaterSensorAsset');

export type ParentPick = { kind: 'found'; asset_id: string } | { kind: 'missing' } | { kind: 'ambiguous'; count: number };

/** The gateway's GroupAsset, matched exactly by name, type and City > Building > Room. The
 *  parent search is a loose ILIKE capped at 25 rows, so the exact filter happens here. */
export function pickParent(matches: ParentAssetMatch[], realm: string, site: OnboardingSite, gateway: OnboardingGateway): ParentPick {
  const exact = matches.filter((m) => {
    const p = m.parent_path;
    const at = (back: number) => p[p.length - back];
    return m.realm === realm && m.asset_type === 'GroupAsset' && m.name === gateway.group && p.length >= 3
      && at(1).type === 'RoomAsset' && at(1).name === gateway.room
      && at(2).type === 'BuildingAsset' && at(2).name === site.building
      && at(3).type === 'CityAsset' && at(3).name === site.city;
  });
  if (exact.length === 1) return { kind: 'found', asset_id: exact[0].asset_id };
  return exact.length === 0 ? { kind: 'missing' } : { kind: 'ambiguous', count: exact.length };
}
